import 'vue-router';

import { createRouter, createWebHistory } from '@ionic/vue-router';

import { RouteRecordRaw } from 'vue-router';
import TabLayout from '@/components/TabLayout.vue';
import { isSignedIn } from '@/services/auth';
import logger from '@/services/logger';
import { useStore } from '@/store';

export {};

declare module 'vue-router' {
  interface RouteMeta {
    isPublic?: boolean;
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/',
    component: TabLayout,
    children: [
      {
        path: '',
        redirect: '/home',
      },
      {
        path: 'home/:reference?',
        name: 'home',
        props: ({ params }) => {
          return {
            reference: params.reference as string | undefined,
          };
        },
        component: () => import('@/views/reports/ReportsPage.vue'),
      },
      {
        path: '/newBooking/:templateId?',
        name: 'newBooking',
        props: ({ params }) => {
          return {
            templateId: params.templateId as string | undefined,
          };
        },
        component: () => import('@/views/booking/NewBookingPage.vue'),
      },
      {
        path: '/profile',
        name: 'profile',
        component: () => import('@/views/profile/ProfilePage.vue'),
      },
    ],
  },
  {
    path: '/signin',
    name: 'signin',
    component: () => import('@/views/auth/SignInPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/sso',
    name: 'sso',
    props: ({ query }) => ({
      // Initial SSO entry
      clientId: query.c as string | undefined,
      templateId: query.t as string | undefined,
      quoteId: query.q as string | undefined,

      // OAuth callback
      code: query.code as string | undefined,
      state: query.state as string | undefined,

      // OAuth error callback
      error: query.error as string | undefined,
      errorDescription: query.error_description as string | undefined,
    }),
    component: () => import('@/views/auth/SSOPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/auth/SignUpPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/verifyCode',
    name: 'verifyCode',
    component: () => import('@/views/auth/VerifyCodePage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/forgotPassword',
    name: 'forgotPassword',
    component: () => import('@/views/auth/ForgotPasswordPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/setPassword/:initialEmail',
    name: 'setPassword',
    props: true,
    component: () => import('@/views/auth/SetPasswordPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/PrivacyPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/TermsPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/selectProfile',
    name: 'selectProfile',
    component: () => import('@/views/auth/SelectProfilePage.vue'),
    meta: { isHybrid: true },
  },
  {
    path: '/changePassword',
    name: 'changePassword',
    component: () => import('@/views/auth/ChangePasswordPage.vue'),
  },
  {
    path: '/viewBooking/:reference',
    name: 'viewBooking',
    props: ({ params }) => ({
      reference: Number.parseInt(params.reference as string) || 0,
    }),
    component: () => import('@/views/booking/ViewBookingPage.vue'),
  },
  {
    path: '/bookingPayment',
    name: 'bookingPayment',
    component: () => import('@/views/booking/BookingPaymentPage.vue'),
  },
  {
    path: '/addCard',
    name: 'addCard',
    component: () => import('@/views/profile/AddCardPage.vue'),
  },
  {
    path: '/editProfile',
    name: 'editProfile',
    component: () => import('@/views/profile/EditProfilePage.vue'),
  },
  {
    path: '/editBooking/:reference',
    name: 'editBooking',
    props: ({ params }) => ({
      reference: Number.parseInt(params.reference as string) || 0,
    }),
    component: () => import('@/views/booking/EditBookingPage.vue'),
  },
  {
    path: '/contactLink',
    name: 'contactLink',
    component: () => import('@/views/profile/ContactLinkPage.vue'),
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/profile/SettingsPage.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/mfaSettings',
    name: 'mfaSettings',
    component: () => import('@/views/auth/MFASettingsPage.vue'),
  },
  {
    path: '/totpSetup/:email?',
    name: 'totpSetup',
    props: ({ params }) => {
      return {
        email: params.email as string | undefined,
      };
    },
    component: () => import('@/views/auth/TOTPSetupPage.vue'),
    meta: { isHybrid: true },
  },
  {
    path: '/smsSetup',
    name: 'smsSetup',
    component: () => import('@/views/auth/SMSSetupPage.vue'),
  },
  {
    path: '/quote/:templateId?',
    name: 'quote',
    props: ({ params, query }) => {
      return {
        templateId: params.templateId as string | undefined,
        quoteId: query.q as string | undefined,
      };
    },
    component: () => import('@/views/booking/QuickQuotePage.vue'),
    meta: { isPublic: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from) => {
  const toFrom = { to: to.name, from: from.name };
  const store = useStore();
  const cognitoSignIn = await isSignedIn();

  if (
    cognitoSignIn &&
    !to.meta.isPublic &&
    !store.integraProfile?.userSession
  ) {
    // User is signed into cognito and accessing private page on app reopen
    await store.$hydrate();
    logger.debug('hydratingStore', toFrom);
  }

  const integraSignIn = store.isSignedIntoIntegra;
  const isFullySignedIn = cognitoSignIn && integraSignIn;
  const isHybridPage = to.meta.isHybrid; // Doesn't always require Integra Auth (select profile, totpSetup, etc.)
  if (
    (!isFullySignedIn && !to.meta.isPublic && !isHybridPage) ||
    (isHybridPage && !from.name)
  ) {
    // User is not fully signed in and accessing private page (other than hybrid pages)
    // OR user reopens app on a hybrid page
    await store.$hydrate();
    logger.debug('navigatingToSignIn', {
      ...toFrom,
      cognitoSignIn,
      integraSignIn,
    });
    return { name: 'signin' };
  }

  logger.debug('navigating', {
    ...toFrom,
    cognitoSignIn,
    integraSignIn,
  });
});

export default router;
